// Work's task caches, patched in place (WORK-PERF; CHAT-PERF's live/cache-patch.ts for chat).
// Two writers: optimistic task writes (task-writes.ts: the row the client expects, then the
// server's copy) and payload-carrying change pings (`task.*` pings carry the task card as the
// list routes serve it to this viewer, so a second browser shows a change with no refetch).
//
// One card reaches every cached place that shows it:
//   - a board's saved-view results (`boards/{id}/tasks?view=`): the view's filter, sort and group
//     re-applied on the client (view-engine.ts, the same engine the fake twin serves), so a status
//     change moves the card to its new column; without the view in cache it is replaced in place;
//   - a board's every-task list, the company's tasks, a parent's subtasks (upsert);
//   - My tasks and search results (replaced in place; a new row there waits for the refetch);
//   - the open drawer's detail (the card's fields merged over it);
//   - a board the task left (moved): removed.
// Cards the client made up (an optimistic create) carry `local` and are never persisted.
import type { OperationOutput } from "@companyos/sdk";
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import {
  type ListData,
  removeRows,
  rowsOf,
  type UpsertOptions,
  upsertRow,
} from "../../live/cache-patch";
import { p1TaskKeys } from "./task-page/queries";
import {
  applyView,
  type Board,
  type FieldDef,
  matches,
  sortCards,
  type TaskCard,
  type TaskDetail,
  type View,
  type ViewContext,
} from "./view-engine";
import { workKeys } from "./work-queries";

/** A card as the client holds it: an optimistic one says so until the server answers. */
export type LocalTask = TaskCard & { local?: { state: "saving" } };
type BoardTasks = OperationOutput<"boards.tasks">;

const taskOptions: UpsertOptions<LocalTask> = {
  aliases: (t) => [t.client_id ? `client:${t.client_id}` : null],
};

/** The same task (by id, or the client id an optimistic row was made with). */
const sameTask = (a: LocalTask, b: LocalTask) =>
  a.id === b.id || (a.client_id !== null && a.client_id === b.client_id);

/** The view a board's cached result was evaluated with, and the context to re-apply it. */
function viewOf(
  queryClient: QueryClient,
  co: string,
  boardId: string,
  viewId: string | null,
): { view: Pick<View, "filter" | "sort" | "group_by">; ctx: ViewContext } | null {
  const views = queryClient.getQueryData<{ data: View[] }>(workKeys.views(co, boardId))?.data;
  const board = queryClient.getQueryData<Board>(workKeys.board(co, boardId));
  if (!views || !board) return null;
  const view =
    views.find((v) => v.id === viewId) ?? views.find((v) => v.is_default) ?? views[0] ?? null;
  if (!view) return null;
  const fields =
    queryClient.getQueryData<{ data: FieldDef[] }>(workKeys.fields(co, boardId))?.data ?? [];
  return { view, ctx: { statuses: board.status_set, fields } };
}

/**
 * A saved view's cached result with `card` written in (or removed: `card` null and `drop` its
 * id). With the view known, the card is re-filtered, re-sorted and re-grouped like the server
 * would; otherwise it is replaced where it is. Pure; the same object when nothing changed.
 */
export function patchBoardTasks(
  d: BoardTasks,
  card: LocalTask | null,
  o: {
    insert: boolean;
    drop?: string;
    view?: { view: Pick<View, "filter" | "sort" | "group_by">; ctx: ViewContext } | null;
  },
): BoardTasks {
  const all = new Map<string, LocalTask>();
  for (const t of [...d.data, ...(d.groups ?? []).flatMap((g) => g.data)] as LocalTask[])
    all.set(t.id, t);
  const present = [...all.values()].find(
    (t) => (card && sameTask(t, card)) || (o.drop !== undefined && t.id === o.drop),
  );
  if (!present && (!card || !o.insert)) return d;
  const v = o.view;
  if (!v) {
    // No view to evaluate: replace in place (or append a new card to the first list).
    const swap = (rows: TaskCard[]) => {
      const at = rows.findIndex((t) => present && t.id === present.id);
      if (at < 0) return rows;
      return card ? rows.map((t, i) => (i === at ? card : t)) : rows.filter((_, i) => i !== at);
    };
    if (!present && card) {
      if (d.groups?.length) {
        const [first, ...rest] = d.groups;
        return {
          ...d,
          groups: [
            {
              ...first,
              data: [...(first?.data ?? []), card],
              count: (first?.count ?? 0) + 1,
            } as typeof first & {},
            ...rest,
          ],
        };
      }
      return { ...d, data: [...d.data, card] };
    }
    return {
      ...d,
      data: swap(d.data),
      groups:
        d.groups?.map((g) => {
          const data = swap(g.data);
          return data === g.data ? g : { ...g, data, count: g.count + data.length - g.data.length };
        }) ?? null,
    };
  }
  if (present) all.delete(present.id);
  if (card && matches(card, v.view.filter, v.ctx)) all.set(card.id, card);
  const rows = [...all.values()];
  if (!d.groups) return { ...d, data: sortCards(rows, v.view.sort, v.ctx) };
  const regrouped = applyView(rows, { ...v.view, filter: {} }, v.ctx);
  const byKey = new Map(regrouped.map((g) => [g.key, g.tasks]));
  const groups = d.groups.map((g) => {
    const data = byKey.get(g.key) ?? [];
    byKey.delete(g.key);
    const same = data.length === g.data.length && data.every((t, i) => t === g.data[i]);
    return same ? g : { ...g, data, count: Math.max(0, g.count + data.length - g.data.length) };
  });
  // A group the server did not send (a new assignee): after the others, as the engine orders.
  for (const g of regrouped)
    if (byKey.has(g.key) && g.tasks.length)
      groups.push({
        key: g.key,
        label: g.label,
        count: g.tasks.length,
        data: g.tasks,
        next_cursor: null,
      });
  return { ...d, groups };
}

/** The detail with the card's fields over it (checklists, dependencies and values kept). */
function mergeDetail(detail: TaskDetail, card: LocalTask): TaskDetail {
  const { local: _local, ...rest } = card;
  const assignees = detail.assignees.filter((a) => rest.assignee_seat_ids.includes(a.seat_id));
  for (const seat of rest.assignee_seat_ids)
    if (!assignees.some((a) => a.seat_id === seat))
      assignees.push({
        company_id: rest.company_id,
        task_id: rest.id,
        seat_id: seat,
        actor_kind: rest.actor_kind,
        actor_membership_id: rest.actor_membership_id,
        created_at: rest.updated_at,
      });
  return { ...detail, ...rest, assignees };
}

export type PatchTaskOptions = {
  /** Add the card where it is missing (a created task); default: only replace shown rows. */
  insert?: boolean;
};

/**
 * Writes one card into every cached place that shows it (see the header). Returns the keys a
 * new row could not be placed in without the server (My tasks), for a background refetch.
 */
export function patchTask(
  queryClient: QueryClient,
  co: string,
  card: LocalTask,
  o: PatchTaskOptions = {},
): QueryKey[] {
  const insert = o.insert ?? false;
  const later: QueryKey[] = [];
  for (const [key, data] of queryClient.getQueriesData({ queryKey: workKeys.all(co) })) {
    if (!data || typeof data !== "object") continue;
    const k = key as readonly unknown[];
    const kind = k[3];
    if (kind === "board" && k[5] === "tasks") {
      const boardId = String(k[4]);
      const d = data as BoardTasks;
      const next =
        boardId === card.board_id
          ? patchBoardTasks(d, card, {
              insert,
              view: viewOf(queryClient, co, boardId, d.view_id),
            })
          : patchBoardTasks(d, null, { insert: false, drop: card.id });
      if (next !== d) queryClient.setQueryData(key, next);
    } else if (kind === "board" && k[5] === "all-tasks") {
      const d = data as ListData<LocalTask>;
      const next =
        String(k[4]) === card.board_id
          ? upsertRow(d, card, { ...taskOptions, insert })
          : removeRows(d, (t) => t.id === card.id);
      if (next !== d) queryClient.setQueryData(key, next);
    } else if (kind === "company-tasks") {
      queryClient.setQueryData(
        key,
        upsertRow(data as ListData<LocalTask>, card, { ...taskOptions, insert }),
      );
    } else if (kind === "task" && k[5] === "subtasks") {
      if (String(k[4]) === card.parent_task_id)
        queryClient.setQueryData(
          key,
          upsertRow(data as ListData<LocalTask>, card, { ...taskOptions, insert }),
        );
      else
        queryClient.setQueryData(
          key,
          removeRows(data as ListData<LocalTask>, (t) => t.id === card.id),
        );
    } else if (kind === "my-tasks" || kind === "search") {
      const d = data as ListData<LocalTask>;
      const next = upsertRow(d, card, { ...taskOptions, insert: false });
      if (next === d && insert && kind === "my-tasks") later.push(key);
      else if (next !== d) queryClient.setQueryData(key, next);
    } else if (kind === "task" && k.length === 5 && String(k[4]) === card.id) {
      queryClient.setQueryData(key, mergeDetail(data as TaskDetail, card));
    } else if (kind === "boards" && k[4] === "summary") {
      // Wave 7: WORK-OS's board counts (Home, the sidebar) are the server's; any task change
      // may move them, so they refetch (persisted, they stayed 0/0 after a new task).
      later.push(key);
    }
  }
  return later;
}

/** Removes a task from every cached list (deleted); its detail is left to 404 on refetch. */
export function removeTask(queryClient: QueryClient, co: string, id: string): void {
  for (const [key, data] of queryClient.getQueriesData({ queryKey: workKeys.all(co) })) {
    if (!data || typeof data !== "object") continue;
    const k = key as readonly unknown[];
    if (k[3] === "board" && k[5] === "tasks") {
      const d = data as BoardTasks;
      const next = patchBoardTasks(d, null, { insert: false, drop: id });
      if (next !== d) queryClient.setQueryData(key, next);
    } else if ("data" in data || "pages" in data) {
      const d = data as ListData<LocalTask>;
      const next = removeRows(d, (t) => t.id === id);
      if (next !== d) queryClient.setQueryData(key, next);
    }
  }
}

/** The freshest cached card of a task (the drawer's detail first, then any list). */
export function cachedTask(
  queryClient: QueryClient,
  co: string,
  id: string,
): LocalTask | undefined {
  const detail = queryClient.getQueryData<TaskDetail>(workKeys.task(co, id));
  if (detail) return detail;
  return cachedTasks(queryClient, co).find((t) => t.id === id);
}

/** Every task card in the Work caches (duplicates included). */
export function cachedTasks(queryClient: QueryClient, co: string): LocalTask[] {
  const out: LocalTask[] = [];
  for (const [key, data] of queryClient.getQueriesData({ queryKey: workKeys.all(co) })) {
    if (!data || typeof data !== "object") continue;
    const k = key as readonly unknown[];
    if (k[3] === "board" && k[5] === "tasks") {
      const d = data as BoardTasks;
      out.push(
        ...(d.data as LocalTask[]),
        ...((d.groups ?? []).flatMap((g) => g.data) as LocalTask[]),
      );
    } else if (
      (k[3] === "board" && k[5] === "all-tasks") ||
      k[3] === "company-tasks" ||
      k[3] === "my-tasks" ||
      (k[3] === "task" && k[5] === "subtasks")
    )
      out.push(...rowsOf(data as ListData<LocalTask>));
  }
  return out;
}

/** Rewrites the cached card of a task with `fn` everywhere it shows. */
export function updateTask(
  queryClient: QueryClient,
  co: string,
  id: string,
  fn: (t: LocalTask) => LocalTask,
): void {
  const t = cachedTask(queryClient, co, id);
  if (t) patchTask(queryClient, co, fn(toCard(t)));
}

/** A card or a detail without its detail-only parts: what lists hold. */
export function toCard(d: LocalTask | TaskDetail): LocalTask {
  if (!("checklist_items" in d)) return d;
  const {
    checklists: _c,
    checklist_items: _i,
    dependencies: _d,
    field_values: _f,
    assignees: _a,
    ...card
  } = d;
  return card;
}

/** Every key a write to tasks touches (cancelled and snapshotted by an optimistic write). */
export const taskKeys = (co: string): QueryKey[] => [workKeys.all(co)];

// Per-task write queue ------------------------------------------------------------------------

const queues = new Map<string, { tail: Promise<unknown>; pending: number }>();

/** Runs `send` after every earlier write of the same task has been answered (task-writes.ts). */
export function queued<T>(taskId: string, send: () => Promise<T>): Promise<T> {
  const q = queues.get(taskId) ?? { tail: Promise.resolve(), pending: 0 };
  q.pending++;
  const run = q.tail.catch(() => {}).then(send);
  q.tail = run;
  queues.set(taskId, q);
  const done = () => {
    q.pending--;
    if (q.pending === 0 && queues.get(taskId) === q) queues.delete(taskId);
  };
  run.then(done, done);
  return run;
}

/** This client's writes of the task still queued or in flight. */
export const writesInFlight = (taskId: string) => queues.get(taskId)?.pending ?? 0;

// Change pings --------------------------------------------------------------------------------

/** What a `task.*` ping carries (live/change-pings.ts `ChangePing`). */
export type TaskPing = {
  subject_kind: string | null;
  subject_id: string | null;
  type: string;
  task?: TaskCard;
};

/**
 * A task ping with its card: written into every cached place now (no refetch). Returns the keys
 * still to refetch around it (the task's history; its checklist items when they changed; My
 * tasks when a new row may belong there), or null when the ping carries no card (a deleted
 * task, or a server without payload pings): it then invalidates by its subject.
 */
export function patchTaskPing(
  queryClient: QueryClient,
  co: string,
  p: TaskPing,
): QueryKey[] | null {
  const card = p.task;
  if (!card) {
    if (p.type === "task.deleted" && p.subject_id) {
      removeTask(queryClient, co, p.subject_id);
      return [workKeys.myTasks(co), ["company", co, "work", "space"]];
    }
    return null;
  }
  const around: QueryKey[] = [workKeys.history(co, card.id), ...p1TaskKeys(co, card.id)];
  // This client's own write is still on its way: its answer reconciles, and an older echo must
  // not overwrite the newer optimistic copy. A copy older than the one shown is stale too.
  const shown = cachedTask(queryClient, co, card.id);
  if (writesInFlight(card.id) > 0 || (shown && !shown.local && shown.version > card.version))
    return around;
  const insert = p.type === "task.created" || p.type === "task.moved";
  around.push(...patchTask(queryClient, co, card, { insert }));
  if (p.type === "task.checklist_changed") around.push(workKeys.task(co, card.id));
  // TASK-FILES: the drawer's files, folders and trash.
  if (p.type.startsWith("task.file_") || p.type === "task.trash_emptied")
    around.push([...workKeys.task(co, card.id), "files"]);
  if (p.type === "task.assigned" || p.type === "task.unassigned") around.push(workKeys.myTasks(co));
  // AGENT-MENTION: a handoff moved (assigned, woken, started, done, dropped).
  if (
    p.type === "task.assigned" ||
    p.type === "task.unassigned" ||
    p.type === "task.agent_wake_changed"
  )
    around.push(workKeys.agentWakes(co, card.id));
  if (p.type === "task.created") around.push(["company", co, "work", "space"]);
  return around;
}

/** A thread's message as a task drawer's conversation holds it (`workKeys.thread`). */
type ThreadMessage = {
  id: string;
  thread_id: string;
  client_id: string | null;
  created_at: string;
};

/**
 * Writes a message into the task conversation that shows its thread (a chat message ping, or an
 * optimistic comment): replaces the same message (or its optimistic copy), else appends it.
 */
export function patchThreadMessage<M extends ThreadMessage>(
  queryClient: QueryClient,
  co: string,
  m: M,
): void {
  queryClient.setQueriesData<{ thread_id: string; messages: M[] }>(
    { queryKey: ["company", co, "work", "thread"] },
    (old) => {
      if (!old || old.thread_id !== m.thread_id) return old;
      const at = old.messages.findIndex(
        (x) => x.id === m.id || (m.client_id !== null && x.client_id === m.client_id),
      );
      const messages =
        at < 0 ? [...old.messages, m] : old.messages.map((x, i) => (i === at ? m : x));
      return { ...old, messages };
    },
  );
}
